import React, { type CSSProperties, type JSX } from "react";
import {
  resolveAdminOperatorExplanationSlot,
  type AdminOperatorExplanationScreen,
  type AdminOperatorExplanationSlot,
} from "../locale/admin-operator-explanations";
import { useAdminDashboardLocale } from "../locale/admin-dashboard-locale-context";

export type AdminOperatorExplanationBlockProps = {
  screen: AdminOperatorExplanationScreen;
  slot: AdminOperatorExplanationSlot;
  style?: CSSProperties;
};

function toContainerStyle(
  placement: "section_summary" | "metric_caption" | "detail_help_block" | "empty_error_guidance",
): CSSProperties {
  if (placement === "section_summary") {
    return {
      display: "grid",
      gap: "8px",
      padding: "14px 16px",
      borderRadius: "18px",
      border: "1px solid rgba(148, 163, 184, 0.22)",
      background: "rgba(248, 250, 252, 0.92)",
    };
  }

  if (placement === "detail_help_block") {
    return {
      display: "grid",
      gap: "8px",
      padding: "12px 14px",
      borderRadius: "14px",
      border: "1px solid rgba(226, 232, 240, 0.92)",
      background: "rgba(248, 250, 252, 0.88)",
    };
  }

  if (placement === "empty_error_guidance") {
    return {
      display: "grid",
      gap: "8px",
      padding: "12px 14px",
      borderRadius: "14px",
      border: "1px dashed rgba(148, 163, 184, 0.45)",
      background: "rgba(255, 255, 255, 0.9)",
    };
  }

  return {
    display: "grid",
    gap: "6px",
  };
}

export function AdminOperatorExplanationBlock(
  props: AdminOperatorExplanationBlockProps,
): JSX.Element | null {
  const { locale } = useAdminDashboardLocale();
  const resolved = resolveAdminOperatorExplanationSlot(locale, props.screen, props.slot);

  if (!resolved || resolved.entries.length === 0) {
    return null;
  }

  return (
    <div style={{ ...toContainerStyle(resolved.placement), ...props.style }}>
      {resolved.entries.map((entry) => (
        <p
          key={entry.key}
          style={{
            margin: 0,
            color: "#475569",
            fontSize: resolved.placement === "metric_caption" ? "0.84rem" : "0.9rem",
            lineHeight: 1.6,
          }}
        >
          <span style={{ color: "#18212b", fontWeight: 700 }}>{entry.label}</span>
          {" "}
          {entry.description}
        </p>
      ))}
    </div>
  );
}
